import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Chip,
  InputAdornment,
  Alert,
  Snackbar,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Skeleton,
  LinearProgress,
  Tooltip,
  Checkbox,
} from '@mui/material';
import {
  Search as SearchIcon,
  Eye,
  Trash2,
  Package,
  TrendingUp,
  DollarSign,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  Upload,
  Download,
  CheckCircle,
  AlertCircle,
  X,
} from 'lucide-react';
import * as XLSX from 'xlsx';
import {
  getInventoryItems,
  deleteInventoryItem,
  updateInventoryItem,
  addInventoryItem,
} from '../firebase/inventoryService';
import { logActivity, ACTIONS, ENTITY_TYPES } from '../firebase/activityLogService';
import LowStockAlertSmall from './LowStockAlertSmall';

// eslint-disable-next-line no-unused-vars
const Inventory = ({ currentUser, permissions }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [inventory, setInventory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({ name: '', category: '', costPrice: '', retailPrice: '', wholesalePrice: '', sellingPrice: '', stock: '' });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [showLowStockOnly, setShowLowStockOnly] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedProduct, setSelectedProduct] = useState('all');
  const [expandedRow, setExpandedRow] = useState(null);
  const [summaryView, setSummaryView] = useState('all');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [editingDiscount, setEditingDiscount] = useState(null); // { itemId, batchId }
  const [discountValue, setDiscountValue] = useState(0);
  const [editingPrice, setEditingPrice] = useState(null); // { itemId, field: 'retailPrice'|'wholesalePrice' }
  const [priceValue, setPriceValue] = useState('');
  
  // Bulk delete state
  const [selectedItems, setSelectedItems] = useState([]);
  const [bulkDeleteConfirm, setBulkDeleteConfirm] = useState(false);

  // Bulk import state
  const fileInputRef = useRef(null);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [importPreview, setImportPreview] = useState([]);
  const [importErrors, setImportErrors] = useState([]);
  const [importing, setImporting] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const [importResult, setImportResult] = useState(null);

  // Check if user can view profit/loss data
  const isAdmin = permissions?.userType === 'admin';
  const canViewProfitLoss = isAdmin || permissions?.viewProfitLoss === true;
  const canDelete = isAdmin || permissions?.deleteInventory === true;

  // Load data once on mount
  const loadInventory = useCallback(async () => {
    setLoading(true);
    const result = await getInventoryItems();
    if (result.success) {
      setInventory(prevInventory => {
        // Intelligent merge - protect critical inventory fields
        const existingMap = new Map(prevInventory.map(item => [item.id, item]));
        
        return result.data.map(newItem => {
          const existing = existingMap.get(newItem.id);
          
          if (!existing) {
            return newItem; // New item, use as-is
          }
          
          // PROTECT IMMUTABLE FIELDS
          return {
            ...newItem,
            // Critical fields that should NEVER change randomly
            name: existing.name,
            category: existing.category,
            costPrice: existing.costPrice,
            createdAt: existing.createdAt,
            // Protect batches array from being emptied by partial snapshots
            batches: (newItem.batches && newItem.batches.length > 0)
              ? newItem.batches
              : existing.batches || [],
          };
        });
      });
    } else {
      showSnackbar(result.error || 'Failed to load inventory', 'error');
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    loadInventory();
  }, [loadInventory]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadInventory();
    setRefreshing(false);
    showSnackbar('Inventory refreshed');
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // ── Bulk Import ──────────────────────────────────────────────────────────

  const downloadTemplate = () => {
    const ws = XLSX.utils.aoa_to_sheet([
      ['Product Name *', 'Item Code', 'Category *', 'Stock Qty *', 'Cost Price (LKR) *', 'Retail Price (LKR) *', 'Wholesale Price (LKR) *'],
      ['Rose Oud 50ml', 'PRF-001', 'Perfume', 25, 1200.00, 2500.00, 2000.00],
      ['Musk Attar 10ml', 'ATT-001', 'Attar', 10, 850.00, 1800.00, 1500.00],
    ]);
    ws['!cols'] = [{ wch: 30 }, { wch: 15 }, { wch: 20 }, { wch: 12 }, { wch: 22 }, { wch: 22 }, { wch: 22 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Opening Stock');
    XLSX.writeFile(wb, 'Inventory_Import_Template.xlsx');
  };

  const exportToExcel = () => {
    const today = new Date().toISOString().split('T')[0];

    // Build rows — always include all columns; cost/margin only if permitted
    const headers = [
      'Name', 'Item Code', 'Category',
      ...(canViewProfitLoss ? ['Cost Price (LKR)'] : []),
      'Retail Price (LKR)', 'Wholesale Price (LKR)',
      ...(canViewProfitLoss ? ['Retail Margin %'] : []),
      'Stock Qty', 'Stock Status',
    ];

    const rows = filteredInventory.map(item => {
      const retailMargin = canViewProfitLoss && item.costPrice > 0
        ? (((item.retailPrice - item.costPrice) / item.costPrice) * 100).toFixed(1)
        : '';
      const stockStatus = item.stock === 0 ? 'Out of Stock' : item.stock <= 10 ? 'Low Stock' : 'In Stock';

      return [
        item.name,
        item.itemCode || '',
        item.category,
        ...(canViewProfitLoss ? [parseFloat(item.costPrice || 0)] : []),
        parseFloat(item.retailPrice || 0),
        parseFloat(item.wholesalePrice || 0),
        ...(canViewProfitLoss ? [retailMargin !== '' ? parseFloat(retailMargin) : ''] : []),
        parseFloat(item.stock || 0),
        stockStatus,
      ];
    });

    // Summary row at the bottom
    const totalItems    = filteredInventory.length;
    const totalStock    = filteredInventory.reduce((s, i) => s + (parseFloat(i.stock) || 0), 0);
    const totalValue    = canViewProfitLoss
      ? filteredInventory.reduce((s, i) => s + (parseFloat(i.costPrice || 0) * parseFloat(i.stock || 0)), 0)
      : null;
    const lowStockCount = filteredInventory.filter(i => i.stock > 0 && i.stock <= 10).length;
    const outOfStock    = filteredInventory.filter(i => i.stock === 0).length;

    const ws = XLSX.utils.aoa_to_sheet([
      // Title rows
      [`INVENTORY REPORT — ${today}`],
      [`Total Items: ${totalItems}   |   Total Stock: ${totalStock} units   |   Low Stock: ${lowStockCount}   |   Out of Stock: ${outOfStock}${totalValue !== null ? `   |   Total Inventory Value: LKR ${totalValue.toLocaleString('en-LK', { minimumFractionDigits: 2 })}` : ''}`],
      [], // blank row
      headers,
      ...rows,
      [], // blank row before summary
      ['SUMMARY', '', '', ...(canViewProfitLoss ? [''] : []), '', '', ...(canViewProfitLoss ? [''] : []), '', ''],
      ['Total Items', totalItems],
      ['Total Stock Units', totalStock],
      ['Low Stock Items', lowStockCount],
      ['Out of Stock Items', outOfStock],
      ...(totalValue !== null ? [['Total Inventory Value (LKR)', totalValue]] : []),
    ]);

    // Column widths
    ws['!cols'] = [
      { wch: 30 }, { wch: 15 }, { wch: 20 },
      ...(canViewProfitLoss ? [{ wch: 18 }] : []),
      { wch: 18 }, { wch: 20 },
      ...(canViewProfitLoss ? [{ wch: 16 }] : []),
      { wch: 12 }, { wch: 14 },
    ];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Inventory');
    XLSX.writeFile(wb, `Inventory_${today}.xlsx`);
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    e.target.value = '';

    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const wb = XLSX.read(evt.target.result, { type: 'binary' });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });

        if (rows.length < 2) { showSnackbar('File is empty or has no data rows', 'error'); return; }

        const headers = rows[0].map(h => String(h).trim().toLowerCase());
        const col = (names) => {
          for (const n of names) {
            const idx = headers.findIndex(h => h.includes(n));
            if (idx !== -1) return idx;
          }
          return -1;
        };

        const nameCol     = col(['product name', 'name', 'product', 'item']);
        const codeCol     = col(['item code', 'itemcode', 'code', 'sku']);
        const catCol      = col(['category', 'cat']);
        const qtyCol      = col(['stock', 'qty', 'quantity']);
        const costCol     = col(['cost price', 'cost']);
        const retailCol   = col(['retail price', 'retail']);
        const wholesaleCol = col(['wholesale price', 'wholesale']);

        if (nameCol === -1 || qtyCol === -1 || costCol === -1) {
          showSnackbar('Required columns not found. Download the template to see the correct format.', 'error');
          return;
        }

        const preview = [];
        const errors = [];

        rows.slice(1).forEach((row, i) => {
          const rowNum = i + 2;
          const name = String(row[nameCol] || '').trim();
          const category = catCol !== -1 ? String(row[catCol] || '').trim() : 'Uncategorized';
          const qty = parseFloat(row[qtyCol]);
          const cost = parseFloat(row[costCol]);
          const retail = retailCol !== -1 ? parseFloat(row[retailCol]) : 0;
          const wholesale = wholesaleCol !== -1 ? parseFloat(row[wholesaleCol]) : 0;
          const itemCode = codeCol !== -1 ? String(row[codeCol] || '').trim() : '';

          if (!name && isNaN(qty) && isNaN(cost)) return; // skip blank rows

          if (!name) { errors.push(`Row ${rowNum}: Product name is empty`); return; }
          if (isNaN(qty) || qty < 0) { errors.push(`Row ${rowNum}: Invalid stock quantity "${row[qtyCol]}"`); return; }
          if (isNaN(cost) || cost < 0) { errors.push(`Row ${rowNum}: Invalid cost price "${row[costCol]}"`); return; }

          // Check if already exists in current inventory
          const exists = inventory.some(
            inv => inv.name.toLowerCase() === name.toLowerCase() &&
                   (inv.category || '').toLowerCase() === (category || '').toLowerCase()
          );

          preview.push({
            name,
            itemCode,
            category: category || 'Uncategorized',
            qty,
            cost,
            retail: isNaN(retail) ? 0 : retail,
            wholesale: isNaN(wholesale) ? 0 : wholesale,
            exists, // will be skipped if true
          });
        });

        setImportPreview(preview);
        setImportErrors(errors);
        setImportResult(null);
        setShowImportDialog(true);
      } catch (err) {
        showSnackbar('Failed to read file. Make sure it is a valid Excel or CSV file.', 'error');
      }
    };
    reader.readAsBinaryString(file);
  };

  const handleConfirmImport = async () => {
    const toImport = importPreview.filter(row => !row.exists);
    if (toImport.length === 0) {
      showSnackbar('No new products to import — all rows already exist in inventory', 'info');
      return;
    }

    setImporting(true);
    setImportProgress(0);

    let created = 0;
    let failed = 0;
    const failedNames = [];

    for (let i = 0; i < toImport.length; i++) {
      const row = toImport[i];
      const today = new Date();
      const dateStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

      const itemData = {
        name: row.name,
        itemCode: row.itemCode || '',
        category: row.category,
        stock: row.qty,
        costPrice: row.cost,
        averageCost: row.cost,
        retailPrice: row.retail,
        wholesalePrice: row.wholesale,
        sellingPrice: row.retail,
        batches: [{
          batchId: `opening-${Date.now()}-${i}`,
          purchaseInvoiceId: 'opening-stock',
          purchaseInvoiceNumber: 'OPENING',
          transferNumber: 'OPENING',
          purchaseDate: dateStr,
          costPrice: row.cost,
          retailPrice: row.retail,
          wholesalePrice: row.wholesale,
          discount: 0,
          quantityPurchased: row.qty,
          quantityRemaining: row.qty,
          supplier: 'Opening Stock',
          source: 'opening_stock',
          createdAt: new Date().toISOString(),
        }],
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      const result = await addInventoryItem(itemData);
      if (result.success) {
        created++;
      } else {
        failed++;
        failedNames.push(row.name);
      }

      setImportProgress(Math.round(((i + 1) / toImport.length) * 100));
    }

    setImporting(false);
    setImportResult({ created, failed, skipped: importPreview.filter(r => r.exists).length, failedNames });
    await loadInventory();
  };

  const closeImportDialog = () => {
    setShowImportDialog(false);
    setImportPreview([]);
    setImportErrors([]);
    setImportResult(null);
    setImportProgress(0);
  };

  const filteredInventory = inventory.filter(item => {
    const matchesSearch = item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.category.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (item.itemCode && item.itemCode.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesStockFilter = !showLowStockOnly || item.stock <= 10;
    
    const matchesCategory = selectedCategory === 'all' || item.category === selectedCategory;
    
    const matchesProduct = selectedProduct === 'all' || item.id === selectedProduct;
    
    return matchesSearch && matchesStockFilter && matchesCategory && matchesProduct;
  });

  // Get unique categories from inventory
  const categories = ['all', ...new Set(inventory.map(item => item.category || 'Uncategorized'))].filter(Boolean);

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      name: item.name,
      category: item.category,
      costPrice: item.costPrice.toString(),
      retailPrice: (item.retailPrice || 0).toString(),
      wholesalePrice: (item.wholesalePrice || 0).toString(),
      sellingPrice: (item.sellingPrice || 0).toString(),
      stock: item.stock.toString(),
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    const item = inventory.find(i => i.id === id);
    if (!item) return;

    // Optimistically update UI
    setInventory(prevInventory => prevInventory.filter(item => item.id !== id));
    showSnackbar('Item deleted successfully');
    
    // Delete from Firestore
    const result = await deleteInventoryItem(id);
    
    if (result.success) {
      // Log the deletion
      await logActivity({
        userId: currentUser?.uid,
        userEmail: currentUser?.email,
        action: ACTIONS.DELETED_INVENTORY_ITEM,
        entityType: ENTITY_TYPES.INVENTORY,
        entityId: id,
        entityName: item.name,
        description: `Deleted inventory item: ${item.name} (${item.category}) - Stock: ${item.stock}`,
        metadata: {
          itemName: item.name,
          category: item.category,
          stock: item.stock,
          costPrice: item.costPrice,
          batchCount: item.batches?.length || 0,
        },
      });
    } else {
      showSnackbar(result.error || 'Failed to sync deletion', 'error');
      await loadInventory();
    }
    
    setDeleteConfirm(null);
  };

  // Bulk delete handlers
  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelectedItems(filteredInventory.map(item => item.id));
    } else {
      setSelectedItems([]);
    }
  };

  const handleSelectItem = (itemId) => {
    setSelectedItems(prev => {
      if (prev.includes(itemId)) {
        return prev.filter(id => id !== itemId);
      } else {
        return [...prev, itemId];
      }
    });
  };

  const handleBulkDelete = async () => {
    if (selectedItems.length === 0) return;

    // Get item details before deletion for logging
    const itemsToDelete = selectedItems.map(itemId => {
      const item = inventory.find(i => i.id === itemId);
      return item ? {
        id: itemId,
        name: item.name,
        category: item.category,
        stock: item.stock,
        costPrice: item.costPrice,
        batchCount: item.batches?.length || 0,
      } : null;
    }).filter(Boolean);

    // Optimistically update UI
    setInventory(prevInventory => prevInventory.filter(item => !selectedItems.includes(item.id)));
    setBulkDeleteConfirm(false);
    showSnackbar(`Deleting ${selectedItems.length} item(s)...`, 'info');

    let successCount = 0;
    let failCount = 0;
    const failedItems = [];

    // Delete each item
    for (const itemId of selectedItems) {
      const result = await deleteInventoryItem(itemId);
      if (result.success) {
        successCount++;
      } else {
        failCount++;
        const item = inventory.find(i => i.id === itemId);
        failedItems.push(item?.name || itemId);
      }
    }

    // Log bulk deletion activity
    if (successCount > 0) {
      await logActivity({
        userId: currentUser?.uid,
        userEmail: currentUser?.email,
        action: ACTIONS.DELETED_INVENTORY_ITEM,
        entityType: ENTITY_TYPES.INVENTORY,
        entityId: 'bulk',
        entityName: `${successCount} items`,
        description: `Bulk deleted ${successCount} inventory item(s)${failCount > 0 ? ` (${failCount} failed)` : ''}`,
        metadata: {
          bulkOperation: true,
          itemCount: successCount,
          failedCount: failCount,
          items: itemsToDelete.filter(item => !failedItems.includes(item.name)).map(item => ({
            id: item.id,
            name: item.name,
            category: item.category,
            stock: item.stock,
          })),
        },
      });
    }

    // Show result
    if (failCount === 0) {
      showSnackbar(`Successfully deleted ${successCount} item(s)`, 'success');
    } else {
      showSnackbar(`Deleted ${successCount} item(s), ${failCount} failed`, 'warning');
      // Reload to sync state
      await loadInventory();
    }

    setSelectedItems([]);
  };

  const handleClearSelection = () => {
    setSelectedItems([]);
  };

  // Handle discount editing
  const startEditingDiscount = (itemId, batchId, currentDiscount) => {
    setEditingDiscount({ itemId, batchId });
    setDiscountValue(currentDiscount || 0);
  };

  const cancelEditingDiscount = () => {
    setEditingDiscount(null);
    setDiscountValue(0);
  };

  // Handle inline price editing
  const startEditingPrice = (itemId, field, currentValue) => {
    setEditingPrice({ itemId, field });
    setPriceValue(currentValue || '');
  };

  const cancelEditingPrice = () => {
    setEditingPrice(null);
    setPriceValue('');
  };

  const savePrice = async (itemId) => {
    const item = inventory.find(i => i.id === itemId);
    if (!item) return;

    const field = editingPrice.field;
    const fieldLabel = field === 'retailPrice' ? 'Retail' : 'Wholesale';
    const priceKey = field === 'retailPrice' ? 'retail' : 'wholesale';

    // Decant items — priceValue is { '5': ..., '10': ... }
    if (item.trackInML) {
      const p5  = parseFloat(priceValue?.['5'])  || 0;
      const p10 = parseFloat(priceValue?.['10']) || 0;
      if (p5 < 0 || p10 < 0) {
        showSnackbar('Please enter valid prices', 'error');
        return;
      }
      const currentPrices = item.decantPrices || {};
      const newDecantPrices = {
        5:  { ...(typeof currentPrices[5]  === 'object' ? currentPrices[5]  : { retail: currentPrices[5]  || 0, wholesale: currentPrices[5]  || 0 }), [priceKey]: p5  },
        10: { ...(typeof currentPrices[10] === 'object' ? currentPrices[10] : { retail: currentPrices[10] || 0, wholesale: currentPrices[10] || 0 }), [priceKey]: p10 },
      };
      const result = await updateInventoryItem(itemId, {
        decantPrices: newDecantPrices,
        [field]: newDecantPrices[5][priceKey],
      });
      if (result.success) {
        setInventory(prev => prev.map(i =>
          i.id === itemId ? { ...i, decantPrices: newDecantPrices, [field]: newDecantPrices[5][priceKey] } : i
        ));
        showSnackbar(`${fieldLabel} prices updated`);
        setEditingPrice(null);
        setPriceValue('');
      } else {
        showSnackbar(result.error || 'Failed to update price', 'error');
      }
      return;
    }

    // Regular items — priceValue is a number string
    const parsed = parseFloat(priceValue);
    if (isNaN(parsed) || parsed < 0) {
      showSnackbar('Please enter a valid price', 'error');
      return;
    }

    // If item has active batches, update the oldest active batch price (FIFO - next to sell)
    if (item.batches && item.batches.length > 0) {
      const activeBatches = item.batches.filter(b => (b.quantityRemaining || 0) > 0);
      if (activeBatches.length > 0) {
        const oldestBatch = [...activeBatches].sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt))[0];
        const updatedBatches = item.batches.map(b =>
          b.batchId === oldestBatch.batchId ? { ...b, [field]: parsed } : b
        );
        const result = await updateInventoryItem(itemId, {
          batches: updatedBatches,
          [field]: parsed,
        });
        if (result.success) {
          setInventory(prev => prev.map(i =>
            i.id === itemId ? { ...i, batches: updatedBatches, [field]: parsed } : i
          ));
          showSnackbar(`${fieldLabel} price updated for current batch (next to sell)`);
          setEditingPrice(null);
          setPriceValue('');
        } else {
          showSnackbar(result.error || 'Failed to update price', 'error');
        }
        return;
      }
    }

    // No batches — update item-level price directly
    const result = await updateInventoryItem(itemId, { [field]: parsed });
    if (result.success) {
      setInventory(prev => prev.map(i =>
        i.id === itemId ? { ...i, [field]: parsed } : i
      ));
      showSnackbar(`${fieldLabel} price updated successfully`);
      setEditingPrice(null);
      setPriceValue('');
    } else {
      showSnackbar(result.error || 'Failed to update price', 'error');
    }
  };

  const saveDiscount = async (itemId, batchId) => {
    try {
      // Find the item
      const item = inventory.find(i => i.id === itemId);
      if (!item) {
        showSnackbar('Item not found', 'error');
        return;
      }

      // Update the batch discount
      const updatedBatches = item.batches.map(batch => 
        batch.batchId === batchId 
          ? { ...batch, discount: parseFloat(discountValue) || 0 }
          : batch
      );

      // Update in Firebase
      const result = await updateInventoryItem(itemId, { batches: updatedBatches });
      
      if (result.success) {
        // Update local state
        setInventory(prevInventory => 
          prevInventory.map(i => 
            i.id === itemId 
              ? { ...i, batches: updatedBatches }
              : i
          )
        );
        showSnackbar('Discount updated successfully');
        setEditingDiscount(null);
        setDiscountValue(0);
      } else {
        showSnackbar(result.error || 'Failed to update discount', 'error');
      }
    } catch (error) {
      console.error('Error updating discount:', error);
      showSnackbar('Failed to update discount', 'error');
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingItem(null);
    setFormData({ name: '', category: '', costPrice: '', retailPrice: '', wholesalePrice: '', sellingPrice: '', stock: '' });
  };

  const calculateProfitMargin = (item) => {
    let price = item.retailPrice || item.sellingPrice || 0;
    if (item.batches && item.batches.length > 0) {
      const active = item.batches.filter(b => (b.quantityRemaining || 0) > 0);
      if (active.length > 0) {
        const oldest = [...active].sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt))[0];
        price = oldest.retailPrice || price;
      }
    }
    if (price === 0) return '0.0';
    return (((price - item.costPrice) / price) * 100).toFixed(1);
  };

  // Get oldest batch price for FIFO display
  const getOldestBatchPrice = (item) => {
    if (!item.batches || item.batches.length === 0) {
      return { retail: item.retailPrice || 0, wholesale: item.wholesalePrice || 0, fromBatch: false };
    }
    const activeBatches = item.batches.filter(b => (b.quantityRemaining || 0) > 0);
    if (activeBatches.length === 0) {
      return { retail: item.retailPrice || 0, wholesale: item.wholesalePrice || 0, fromBatch: false };
    }
    const oldestBatch = [...activeBatches].sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt))[0];
    return {
      retail: oldestBatch.retailPrice || item.retailPrice || 0,
      wholesale: oldestBatch.wholesalePrice || item.wholesalePrice || 0,
      fromBatch: true,
      batchCount: activeBatches.length,
    };
  };

  // Calculate stats based on summary view
  const getDisplayItems = () => {
    if (summaryView === 'all') {
      return inventory;
    }
    return inventory.filter(item => item.id === summaryView);
  };

  const displayItems = getDisplayItems();
  const totalItems = displayItems.length;
  const totalStock = displayItems.reduce((sum, item) => sum + (parseFloat(item.stock) || 0), 0);
  const totalInventoryValue = displayItems.reduce((sum, item) => {
    const cost = parseFloat(item.costPrice) || 0;
    const stock = parseFloat(item.stock) || 0;
    return sum + (cost * stock);
  }, 0);
  
  const potentialProfit = displayItems.reduce((sum, item) => {
    let sellingPrice = parseFloat(item.sellingPrice) || 0;
    if (item.batches && item.batches.length > 0) {
      const active = item.batches.filter(b => (b.quantityRemaining || 0) > 0);
      if (active.length > 0) {
        const oldest = [...active].sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt))[0];
        sellingPrice = parseFloat(oldest.retailPrice) || sellingPrice;
      }
    }
    const cost = parseFloat(item.costPrice) || 0;
    const stock = parseFloat(item.stock) || 0;
    const profit = (sellingPrice - cost) * stock;
    return sum + (isNaN(profit) ? 0 : profit);
  }, 0);

  const StatCard = ({ icon, value, label, color, subtitle }) => (
    <Card sx={{ height: '100%', position: 'relative', overflow: 'visible' }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box
            sx={{
              p: 1.5,
              borderRadius: 2,
              backgroundColor: 'action.hover',
              color: 'text.primary',
              display: 'flex',
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            {icon}
          </Box>
        </Box>
        <Typography variant="h4" fontWeight={700} gutterBottom color="text.primary">
          {value}
        </Typography>
        <Typography variant="body2" color="text.secondary" fontWeight={600}>
          {label}
        </Typography>
        {subtitle && (
          <Typography variant="caption" color="text.secondary">
            {subtitle}
          </Typography>
        )}
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <Box>
        <Box sx={{ mb: 4 }}>
          <Skeleton variant="text" width={300} height={50} />
          <Skeleton variant="text" width={400} height={30} />
        </Box>
        
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {[1, 2, 3, 4].map((i) => (
            <Grid item xs={12} sm={6} md={3} key={i}>
              <Card>
                <CardContent>
                  <Skeleton variant="rectangular" width={40} height={40} sx={{ mb: 2, borderRadius: 2 }} />
                  <Skeleton variant="text" width="60%" height={40} />
                  <Skeleton variant="text" width="80%" />
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        <Card>
          <CardContent>
            <Skeleton variant="text" width={200} height={40} sx={{ mb: 3 }} />
            <Skeleton variant="rectangular" width="100%" height={60} sx={{ mb: 3, borderRadius: 1 }} />
            <Skeleton variant="rectangular" width="100%" height={400} sx={{ borderRadius: 1 }} />
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" fontWeight={700} gutterBottom>
            Shop Inventory
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage your shop products, pricing, and stock levels
          </Typography>
        </Box>
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>Summary View</InputLabel>
          <Select
            value={summaryView}
            label="Summary View"
            onChange={(e) => setSummaryView(e.target.value)}
          >
            <MenuItem value="all">All Items Summary</MenuItem>
            {inventory.map((item) => (
              <MenuItem key={item.id} value={item.id}>
                {item.name} ({item.category})
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <LowStockAlertSmall />

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={canViewProfitLoss ? 3 : 6}>
          <StatCard
            icon={<Package size={24} />}
            value={totalItems}
            label="Total Items"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={canViewProfitLoss ? 3 : 6}>
          <StatCard
            icon={<Package size={24} />}
            value={totalStock}
            label="Total Stock"
          />
        </Grid>
        {canViewProfitLoss && (
          <>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                icon={<DollarSign size={24} />}
                value={`LKR ${totalInventoryValue.toLocaleString('en-LK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                label="Inventory Value"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                icon={<TrendingUp size={24} />}
                value={`LKR ${potentialProfit.toLocaleString('en-LK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                label="Potential Profit"
              />
            </Grid>
          </>
        )}
      </Grid>

      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
            <Typography variant="h6" fontWeight={600}>
              All Items
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              {selectedItems.length > 0 && canDelete && (
                <>
                  <Chip
                    label={`${selectedItems.length} selected`}
                    color="primary"
                    onDelete={handleClearSelection}
                    deleteIcon={<X size={16} />}
                  />
                  <Button
                    variant="contained"
                    color="error"
                    startIcon={<Trash2 size={18} />}
                    onClick={() => setBulkDeleteConfirm(true)}
                    size="small"
                  >
                    Delete Selected
                  </Button>
                </>
              )}
              <Button
                variant="outlined"
                startIcon={<Download size={18} />}
                onClick={downloadTemplate}
                size="small"
                color="primary"
              >
                Template
              </Button>
              <Button
                variant="outlined"
                startIcon={<Download size={18} />}
                onClick={exportToExcel}
                size="small"
                color="success"
              >
                Export Excel
              </Button>
              <Button
                variant="outlined"
                startIcon={<Upload size={18} />}
                onClick={() => fileInputRef.current?.click()}
                size="small"
                color="primary"
              >
                Import Stock
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls,.csv"
                style={{ display: 'none' }}
                onChange={handleFileUpload}
              />
              <Button
                variant={showLowStockOnly ? 'contained' : 'outlined'}
                onClick={() => setShowLowStockOnly(!showLowStockOnly)}
                size="small"
                color={showLowStockOnly ? 'warning' : 'primary'}
              >
                {showLowStockOnly ? 'Show All' : 'Low Stock Only'}
              </Button>
              <Button
                variant="outlined"
                startIcon={refreshing ? <CircularProgress size={20} /> : <RefreshCw size={18} />}
                onClick={handleRefresh}
                disabled={refreshing}
                size="small"
                color="primary"
              >
                <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Refresh</Box>
              </Button>
            </Box>
          </Box>

          <TextField
            fullWidth
            placeholder="Search by name, category, or item ID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ mb: 2 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon size={20} />
                </InputAdornment>
              ),
            }}
          />

          {/* Product Filter Dropdown */}
          <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'center', flexWrap: 'wrap' }}>
            <FormControl sx={{ minWidth: 250 }}>
              <InputLabel>Select Product</InputLabel>
              <Select
                value={selectedProduct}
                label="Select Product"
                onChange={(e) => setSelectedProduct(e.target.value)}
              >
                <MenuItem value="all">All Products</MenuItem>
                {inventory.map((item) => (
                  <MenuItem key={item.id} value={item.id}>
                    {item.name} ({item.category}) - Stock: {item.stock}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {selectedProduct !== 'all' && (
              <Button
                variant="outlined"
                size="small"
                onClick={() => setSelectedProduct('all')}
                color="primary"
              >
                Clear Filter
              </Button>
            )}
          </Box>

          {/* Category Filter Chips */}
          <Box sx={{ display: 'flex', gap: 1, mb: 3, flexWrap: 'wrap', alignItems: 'center' }}>
            <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
              Category:
            </Typography>
            {categories.map((category) => (
              <Chip
                key={category}
                label={category === 'all' ? 'All Categories' : category}
                onClick={() => setSelectedCategory(category)}
                color={selectedCategory === category ? 'primary' : 'default'}
                variant={selectedCategory === category ? 'filled' : 'outlined'}
                size="small"
              />
            ))}
          </Box>

          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  {canDelete && (
                    <TableCell padding="checkbox">
                      <Checkbox
                        indeterminate={selectedItems.length > 0 && selectedItems.length < filteredInventory.length}
                        checked={filteredInventory.length > 0 && selectedItems.length === filteredInventory.length}
                        onChange={handleSelectAll}
                      />
                    </TableCell>
                  )}
                  <TableCell><strong>Name</strong></TableCell>
                  <TableCell><strong>Item Code</strong></TableCell>
                  <TableCell><strong>Category</strong></TableCell>
                  {canViewProfitLoss && <TableCell><strong>Cost Price</strong></TableCell>}
                  <TableCell><strong>Retail Price</strong></TableCell>
                  <TableCell><strong>Wholesale Price</strong></TableCell>
                  {canViewProfitLoss && <TableCell><strong>Retail Margin %</strong></TableCell>}
                  <TableCell align="center"><strong>Stock</strong></TableCell>
                  <TableCell align="center"><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredInventory.map((item) => (
                  <React.Fragment key={item.id}>
                  <TableRow 
                    hover
                    sx={{
                      backgroundColor: 'inherit',
                      '&:hover': {
                        backgroundColor: 'rgba(196, 30, 58, 0.08)',
                      }
                    }}
                  >
                    {canDelete && (
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedItems.includes(item.id)}
                          onChange={() => handleSelectItem(item.id)}
                        />
                      </TableCell>
                    )}
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>
                        {item.name}
                      </Typography>
                      {item.isInternal && (
                        <Chip label="Internal" size="small" color="warning" variant="outlined" sx={{ mt: 0.5, fontSize: '0.65rem', height: 18 }} />
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {item.itemCode || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>{item.category}</TableCell>
                    {canViewProfitLoss && (
                      <TableCell>LKR {item.costPrice.toLocaleString('en-LK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</TableCell>
                    )}
                    <TableCell>
                      {/* RETAIL PRICE CELL */}
                      {editingPrice?.itemId === item.id && editingPrice?.field === 'retailPrice' ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          {item.trackInML ? (
                            <>
                              <TextField size="small" type="number" label="5ml Retail" autoFocus
                                value={priceValue?.['5'] ?? ''}
                                onChange={(e) => setPriceValue((v) => ({ ...v, '5': e.target.value }))}
                                onKeyDown={(e) => { if (e.key === 'Enter') savePrice(item.id); if (e.key === 'Escape') cancelEditingPrice(); }}
                                sx={{ width: 120, '& input[type=number]': { MozAppearance: 'textfield' }, '& input[type=number]::-webkit-outer-spin-button': { WebkitAppearance: 'none' }, '& input[type=number]::-webkit-inner-spin-button': { WebkitAppearance: 'none' } }}
                              />
                              <TextField size="small" type="number" label="10ml Retail"
                                value={priceValue?.['10'] ?? ''}
                                onChange={(e) => setPriceValue((v) => ({ ...v, '10': e.target.value }))}
                                onKeyDown={(e) => { if (e.key === 'Enter') savePrice(item.id); if (e.key === 'Escape') cancelEditingPrice(); }}
                                sx={{ width: 120, '& input[type=number]': { MozAppearance: 'textfield' }, '& input[type=number]::-webkit-outer-spin-button': { WebkitAppearance: 'none' }, '& input[type=number]::-webkit-inner-spin-button': { WebkitAppearance: 'none' } }}
                              />
                            </>
                          ) : (
                            <TextField size="small" type="number" autoFocus
                              value={priceValue ?? ''}
                              onChange={(e) => setPriceValue(e.target.value)}
                              inputProps={{ min: 0, step: 0.01 }}
                              onKeyDown={(e) => { if (e.key === 'Enter') savePrice(item.id); if (e.key === 'Escape') cancelEditingPrice(); }}
                              sx={{ width: 100 }}
                            />
                          )}
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            <Button size="small" variant="contained" color="success" onClick={() => savePrice(item.id)} sx={{ minWidth: 'auto', px: 1, py: 0.5, fontSize: '0.7rem' }}>Save</Button>
                            <Button size="small" variant="outlined" color="error" onClick={cancelEditingPrice} sx={{ minWidth: 'auto', px: 1, py: 0.5, fontSize: '0.7rem' }}>Cancel</Button>
                          </Box>
                        </Box>
                      ) : (
                        <Box
                          sx={{ cursor: isAdmin ? 'pointer' : 'default' }}
                          onClick={() => {
                            if (!isAdmin) return;
                            if (item.trackInML) {
                              startEditingPrice(item.id, 'retailPrice', null);
                              setPriceValue({
                                '5':  item.decantPrices?.[5]?.retail  || item.decantPrices?.[5]  || '',
                                '10': item.decantPrices?.[10]?.retail || item.decantPrices?.[10] || '',
                              });
                            } else {
                              const prices = getOldestBatchPrice(item);
                              startEditingPrice(item.id, 'retailPrice', prices.retail);
                            }
                          }}
                        >
                          {item.trackInML ? (
                            <>
                              <Typography variant="body2">5ml: LKR {(item.decantPrices?.[5]?.retail || item.decantPrices?.[5] || 0).toLocaleString('en-LK', { minimumFractionDigits: 2 })}</Typography>
                              <Typography variant="body2">10ml: LKR {(item.decantPrices?.[10]?.retail || item.decantPrices?.[10] || 0).toLocaleString('en-LK', { minimumFractionDigits: 2 })}</Typography>
                            </>
                          ) : (
                            (() => {
                              const prices = getOldestBatchPrice(item);
                              return (
                                <Box>
                                  <Typography variant="body2">
                                    {prices.retail ? `LKR ${prices.retail.toLocaleString('en-LK', { minimumFractionDigits: 2 })}` : 'Not set'}
                                  </Typography>
                                  {prices.fromBatch && <Typography variant="caption" color="primary.main">(Next to sell)</Typography>}
                                </Box>
                              );
                            })()
                          )}
                          {isAdmin && <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem', display: 'block' }}>(click to edit)</Typography>}
                        </Box>
                      )}
                    </TableCell>
                    <TableCell>
                      {/* WHOLESALE PRICE CELL */}
                      {editingPrice?.itemId === item.id && editingPrice?.field === 'wholesalePrice' ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          {item.trackInML ? (
                            <>
                              <TextField size="small" type="number" label="5ml Wholesale" autoFocus
                                value={priceValue?.['5'] ?? ''}
                                onChange={(e) => setPriceValue((v) => ({ ...v, '5': e.target.value }))}
                                onKeyDown={(e) => { if (e.key === 'Enter') savePrice(item.id); if (e.key === 'Escape') cancelEditingPrice(); }}
                                sx={{ width: 120, '& input[type=number]': { MozAppearance: 'textfield' }, '& input[type=number]::-webkit-outer-spin-button': { WebkitAppearance: 'none' }, '& input[type=number]::-webkit-inner-spin-button': { WebkitAppearance: 'none' } }}
                              />
                              <TextField size="small" type="number" label="10ml Wholesale"
                                value={priceValue?.['10'] ?? ''}
                                onChange={(e) => setPriceValue((v) => ({ ...v, '10': e.target.value }))}
                                onKeyDown={(e) => { if (e.key === 'Enter') savePrice(item.id); if (e.key === 'Escape') cancelEditingPrice(); }}
                                sx={{ width: 120, '& input[type=number]': { MozAppearance: 'textfield' }, '& input[type=number]::-webkit-outer-spin-button': { WebkitAppearance: 'none' }, '& input[type=number]::-webkit-inner-spin-button': { WebkitAppearance: 'none' } }}
                              />
                            </>
                          ) : (
                            <TextField size="small" type="number" autoFocus
                              value={priceValue ?? ''}
                              onChange={(e) => setPriceValue(e.target.value)}
                              inputProps={{ min: 0, step: 0.01 }}
                              onKeyDown={(e) => { if (e.key === 'Enter') savePrice(item.id); if (e.key === 'Escape') cancelEditingPrice(); }}
                              sx={{ width: 100 }}
                            />
                          )}
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            <Button size="small" variant="contained" color="success" onClick={() => savePrice(item.id)} sx={{ minWidth: 'auto', px: 1, py: 0.5, fontSize: '0.7rem' }}>Save</Button>
                            <Button size="small" variant="outlined" color="error" onClick={cancelEditingPrice} sx={{ minWidth: 'auto', px: 1, py: 0.5, fontSize: '0.7rem' }}>Cancel</Button>
                          </Box>
                        </Box>
                      ) : (
                        <Box
                          sx={{ cursor: isAdmin ? 'pointer' : 'default' }}
                          onClick={() => {
                            if (!isAdmin) return;
                            if (item.trackInML) {
                              startEditingPrice(item.id, 'wholesalePrice', null);
                              setPriceValue({
                                '5':  item.decantPrices?.[5]?.wholesale  || item.decantPrices?.[5]  || '',
                                '10': item.decantPrices?.[10]?.wholesale || item.decantPrices?.[10] || '',
                              });
                            } else {
                              const prices = getOldestBatchPrice(item);
                              startEditingPrice(item.id, 'wholesalePrice', prices.wholesale);
                            }
                          }}
                        >
                          {item.trackInML ? (
                            <>
                              <Typography variant="body2">5ml: LKR {(item.decantPrices?.[5]?.wholesale || item.decantPrices?.[5] || 0).toLocaleString('en-LK', { minimumFractionDigits: 2 })}</Typography>
                              <Typography variant="body2">10ml: LKR {(item.decantPrices?.[10]?.wholesale || item.decantPrices?.[10] || 0).toLocaleString('en-LK', { minimumFractionDigits: 2 })}</Typography>
                            </>
                          ) : (
                            (() => {
                              const prices = getOldestBatchPrice(item);
                              return (
                                <Box>
                                  <Typography variant="body2">
                                    {prices.wholesale ? `LKR ${prices.wholesale.toLocaleString('en-LK', { minimumFractionDigits: 2 })}` : 'Not set'}
                                  </Typography>
                                  {prices.fromBatch && <Typography variant="caption" color="primary.main">(Next to sell)</Typography>}
                                </Box>
                              );
                            })()
                          )}
                          {isAdmin && <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem', display: 'block' }}>(click to edit)</Typography>}
                        </Box>
                      )}
                    </TableCell>
                    {canViewProfitLoss && (
                      <TableCell>
                        {item.retailPrice && item.retailPrice > 0 ? (
                          <Typography variant="body2" color="success.main" fontWeight={600}>
                            {calculateProfitMargin(item)}%
                          </Typography>
                        ) : (
                          <Typography variant="body2" color="text.secondary">-</Typography>
                        )}
                      </TableCell>
                    )}
                    <TableCell align="center">
                      {item.trackInML ? (
                        <Chip
                          label={`${item.stock} ml`}
                          size="small"
                          color={item.stock > 50 ? 'success' : item.stock > 10 ? 'warning' : 'error'}
                        />
                      ) : (
                        <Chip
                          label={item.stock}
                          size="small"
                          color={item.stock > 20 ? 'success' : item.stock > 0 ? 'warning' : 'error'}
                        />
                      )}
                    </TableCell>
                    <TableCell align="center">
                      {item.batches && item.batches.length > 0 && (
                        <IconButton
                          size="small"
                          onClick={() => setExpandedRow(expandedRow === item.id ? null : item.id)}
                          title="View batch details"
                        >
                          {expandedRow === item.id ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                        </IconButton>
                      )}
                      <IconButton
                        size="small"
                        onClick={() => handleEdit(item)}
                        title="View item details"
                      >
                        <Eye size={18} />
                      </IconButton>
                      {canDelete && (
                        <IconButton
                          size="small"
                          onClick={() => setDeleteConfirm(item)}
                          title="Delete item"
                        >
                          <Trash2 size={18} />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                  {expandedRow === item.id && item.batches && item.batches.length > 0 && (
                    <TableRow>
                      <TableCell colSpan={canViewProfitLoss ? (canDelete ? 9 : 8) : (canDelete ? 7 : 6)} sx={{ bgcolor: 'action.hover', p: 3 }}>
                        <Typography variant="subtitle2" fontWeight={600} gutterBottom sx={{ mb: 2 }}>
                          📦 Batch Details ({item.batches.length} {item.batches.length === 1 ? 'batch' : 'batches'})
                        </Typography>
                        <TableContainer component={Paper} variant="outlined">
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell><strong>Batch</strong></TableCell>
                                <TableCell><strong>Transfer Date</strong></TableCell>
                                <TableCell><strong>GRN #</strong></TableCell>
                                <TableCell><strong>Transfer #</strong></TableCell>
                                {canViewProfitLoss && <TableCell><strong>Cost Price</strong></TableCell>}
                                <TableCell><strong>Retail Price</strong></TableCell>
                                <TableCell><strong>Wholesale Price</strong></TableCell>
                                <TableCell><strong>Discount %</strong></TableCell>
                                <TableCell><strong>Quantity</strong></TableCell>
                                <TableCell><strong>Status</strong></TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {(() => {
                                const sorted = [...item.batches].sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
                                const nextToSellBatch = sorted.find(b => (b.quantityRemaining || 0) > 0);
                                return sorted.map((batch, idx) => {
                                  const isNextToSell = nextToSellBatch?.batchId === batch.batchId;
                                  return (
                                  <TableRow key={batch.batchId} sx={{ '&:hover': { bgcolor: 'action.selected' } }}>
                                    <TableCell>
                                      <Chip 
                                        label={`Batch ${idx + 1}`} 
                                        size="small" 
                                        color={idx === 0 ? 'primary' : 'default'}
                                        variant={idx === 0 ? 'filled' : 'outlined'}
                                      />
                                      {isNextToSell && (
                                        <Typography variant="caption" color="primary.main" display="block">
                                          (Next to sell)
                                        </Typography>
                                      )}
                                    </TableCell>
                                    <TableCell>{batch.purchaseDate || 'N/A'}</TableCell>
                                    <TableCell>
                                      <Chip label={batch.purchaseInvoiceNumber || '—'} size="small" variant="outlined" color="info" />
                                    </TableCell>
                                    <TableCell>
                                      <Chip label={batch.transferNumber || '—'} size="small" variant="outlined" color="primary" />
                                    </TableCell>
                                    {canViewProfitLoss && (
                                      <TableCell>
                                        <Typography variant="body2" fontWeight={600}>
                                          LKR {(batch.costPrice || 0).toFixed(2)}
                                        </Typography>
                                      </TableCell>
                                    )}
                                    <TableCell>
                                      <Typography variant="body2" color="success.main" fontWeight={600}>
                                        LKR {(batch.retailPrice || 0).toFixed(2)}
                                      </Typography>
                                    </TableCell>
                                    <TableCell>
                                      <Typography variant="body2" color="info.main" fontWeight={600}>
                                        LKR {(batch.wholesalePrice || 0).toFixed(2)}
                                      </Typography>
                                    </TableCell>
                                    <TableCell>
                                      {isAdmin && editingDiscount?.itemId === item.id && editingDiscount?.batchId === batch.batchId ? (
                                        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                                          <TextField
                                            type="number"
                                            size="small"
                                            value={discountValue}
                                            onChange={(e) => setDiscountValue(e.target.value)}
                                            inputProps={{ min: 0, max: 100, step: 0.1 }}
                                            sx={{ width: 70 }}
                                            autoFocus
                                            onKeyPress={(e) => {
                                              if (e.key === 'Enter') saveDiscount(item.id, batch.batchId);
                                            }}
                                          />
                                          <Button
                                            size="small"
                                            variant="contained"
                                            color="success"
                                            onClick={() => saveDiscount(item.id, batch.batchId)}
                                            sx={{ minWidth: 'auto', px: 1, py: 0.5, fontSize: '0.7rem' }}
                                          >
                                            Save
                                          </Button>
                                          <Button
                                            size="small"
                                            variant="outlined"
                                            color="error"
                                            onClick={cancelEditingDiscount}
                                            sx={{ minWidth: 'auto', px: 1, py: 0.5, fontSize: '0.7rem' }}
                                          >
                                            Cancel
                                          </Button>
                                        </Box>
                                      ) : (
                                        <Box 
                                          sx={{ 
                                            display: 'flex', 
                                            alignItems: 'center', 
                                            gap: 0.5,
                                            cursor: isAdmin ? 'pointer' : 'default'
                                          }}
                                          onClick={() => isAdmin && startEditingDiscount(item.id, batch.batchId, batch.discount)}
                                        >
                                          <Chip 
                                            label={`${batch.discount || 0}%`}
                                            size="small"
                                            color={batch.discount > 0 ? 'warning' : 'default'}
                                            variant={batch.discount > 0 ? 'filled' : 'outlined'}
                                          />
                                          {isAdmin && (
                                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                                              (click to edit)
                                            </Typography>
                                          )}
                                        </Box>
                                      )}
                                    </TableCell>
                                    <TableCell>
                                      <Chip 
                                        label={`${batch.quantityRemaining || 0}/${batch.quantityPurchased || 0}`}
                                        size="small"
                                        color={batch.quantityRemaining > 0 ? 'success' : 'default'}
                                      />
                                    </TableCell>
                                    <TableCell>
                                      {batch.restored ? (
                                        <Chip 
                                          label="Restored" 
                                          size="small" 
                                          color="warning"
                                          sx={{
                                            fontWeight: 700,
                                            '& .MuiChip-label': {
                                              px: 1,
                                            }
                                          }}
                                        />
                                      ) : batch.quantityRemaining === 0 ? (
                                        <Chip 
                                          label="Out of Stock" 
                                          size="small" 
                                          color="error"
                                          sx={{
                                            fontWeight: 700,
                                            '& .MuiChip-label': {
                                              px: 1,
                                            }
                                          }}
                                        />
                                      ) : (
                                        <Chip 
                                          label="Active" 
                                          size="small" 
                                          color="success"
                                          sx={{
                                            fontWeight: 700,
                                            '& .MuiChip-label': {
                                              px: 1,
                                            }
                                          }}
                                        />
                                      )}
                                    </TableCell>
                                  </TableRow>
                                  );
                                });
                              })()}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </TableCell>
                    </TableRow>
                  )}
                  </React.Fragment>
                ))}
                {filteredInventory.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={canViewProfitLoss ? (canDelete ? 9 : 8) : (canDelete ? 7 : 6)} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">No items found</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      <Dialog open={showModal} onClose={closeModal} maxWidth="sm" fullWidth>
        <DialogTitle>
          View Item Details
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            {editingItem && editingItem.batches && editingItem.batches.length > 0 && (
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2" fontWeight={600} gutterBottom>
                  📦 This item uses batch-based pricing
                </Typography>
                <Typography variant="caption" display="block">
                  • Prices are set per batch in Purchase Invoice
                </Typography>
                <Typography variant="caption" display="block">
                  • Each shipment can have different prices
                </Typography>
                <Typography variant="caption" display="block">
                  • Expand row in table to see all batch details
                </Typography>
              </Alert>
            )}
            <Alert severity="warning" sx={{ mb: 2 }}>
              <Typography variant="body2" fontWeight={600}>
                Inventory is Read-Only
              </Typography>
              <Typography variant="caption" display="block">
                All items must be added through Purchase Invoice to ensure proper batch tracking and pricing.
              </Typography>
            </Alert>
            <TextField
              fullWidth
              label="Item Name"
              value={formData.name}
              sx={{ mb: 2 }}
              InputProps={{
                readOnly: true,
              }}
              disabled
            />
            <TextField
              fullWidth
              label="Category"
              value={formData.category}
              sx={{ mb: 2 }}
              InputProps={{
                readOnly: true,
              }}
              disabled
            />
            <TextField
              fullWidth
              label="Cost Price (LKR)"
              type="number"
              value={formData.costPrice}
              InputProps={{
                startAdornment: <InputAdornment position="start">LKR</InputAdornment>,
                readOnly: true,
              }}
              sx={{ mb: 2 }}
              disabled
            />
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Retail Price (LKR)"
                  type="number"
                  value={formData.retailPrice}
                  disabled
                  helperText={
                    editingItem && editingItem.batches && editingItem.batches.length > 0
                      ? "Prices managed per batch in Purchase Invoice"
                      : "View only - set in Purchase Invoice"
                  }
                  InputProps={{
                    startAdornment: <InputAdornment position="start">LKR</InputAdornment>,
                    readOnly: true,
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Wholesale Price (LKR)"
                  type="number"
                  value={formData.wholesalePrice}
                  disabled
                  helperText={
                    editingItem && editingItem.batches && editingItem.batches.length > 0
                      ? "Prices managed per batch in Purchase Invoice"
                      : "View only - set in Purchase Invoice"
                  }
                  InputProps={{
                    startAdornment: <InputAdornment position="start">LKR</InputAdornment>,
                    readOnly: true,
                  }}
                />
              </Grid>
            </Grid>
            <TextField
              fullWidth
              label="Stock Quantity"
              type="number"
              value={formData.stock}
              sx={{ mb: 2 }}
              disabled
              InputProps={{
                readOnly: true,
              }}
              helperText="Stock updated automatically through sales and restocking"
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button 
            onClick={closeModal} 
            variant="contained"
            sx={{
              backgroundColor: 'error.main',
              color: 'common.white',
              '&:hover': {
                backgroundColor: 'error.dark'
              }
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Item?</DialogTitle>
        <DialogContent>
          <Typography>Delete <strong>{deleteConfirm?.name}</strong>? This cannot be undone.</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteConfirm(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={() => handleDelete(deleteConfirm?.id)}>Delete</Button>
        </DialogActions>
      </Dialog>

      {/* ── Bulk Delete Confirmation Dialog ── */}
      <Dialog open={bulkDeleteConfirm} onClose={() => setBulkDeleteConfirm(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Trash2 size={20} color="#d32f2f" />
            Delete {selectedItems.length} Item(s)?
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2" fontWeight={600} gutterBottom>
              This action cannot be undone!
            </Typography>
            <Typography variant="caption">
              All selected items and their batch history will be permanently deleted.
            </Typography>
          </Alert>
          <Typography variant="body2" gutterBottom fontWeight={600}>
            Items to be deleted:
          </Typography>
          <Box sx={{ maxHeight: 300, overflow: 'auto', mt: 1 }}>
            {selectedItems.map(itemId => {
              const item = inventory.find(i => i.id === itemId);
              return item ? (
                <Box
                  key={itemId}
                  sx={{
                    p: 1.5,
                    mb: 1,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <Box>
                    <Typography variant="body2" fontWeight={600}>
                      {item.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {item.category} • Stock: {item.stock}
                    </Typography>
                  </Box>
                  <Chip
                    label={item.batches?.length || 0}
                    size="small"
                    color="default"
                    sx={{ fontSize: '0.7rem' }}
                  />
                </Box>
              ) : null;
            })}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setBulkDeleteConfirm(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleBulkDelete}
            startIcon={<Trash2 size={16} />}
          >
            Delete {selectedItems.length} Item(s)
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Bulk Import Dialog ── */}
      <Dialog open={showImportDialog} onClose={closeImportDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Upload size={20} />
            Import Opening Stock
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {importResult ? (
            // ── Result screen ──
            <Box sx={{ py: 2 }}>
              <Alert
                severity={importResult.failed === 0 ? 'success' : 'warning'}
                sx={{ mb: 2 }}
              >
                Import complete — {importResult.created} product(s) added
                {importResult.skipped > 0 && `, ${importResult.skipped} skipped (already exist)`}
                {importResult.failed > 0 && `, ${importResult.failed} failed`}
              </Alert>
              {importResult.failedNames.length > 0 && (
                <Alert severity="error">
                  Failed to import: {importResult.failedNames.join(', ')}
                </Alert>
              )}
            </Box>
          ) : (
            <>
              {/* Errors */}
              {importErrors.length > 0 && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  <strong>{importErrors.length} row(s) with errors (will be skipped):</strong>
                  <ul style={{ margin: '4px 0 0', paddingLeft: 16 }}>
                    {importErrors.map((e, i) => <li key={i}>{e}</li>)}
                  </ul>
                </Alert>
              )}

              {/* Summary */}
              <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                <Chip
                  icon={<CheckCircle size={14} />}
                  label={`${importPreview.filter(r => !r.exists).length} will be imported`}
                  color="success" size="small"
                />
                {importPreview.filter(r => r.exists).length > 0 && (
                  <Chip
                    icon={<AlertCircle size={14} />}
                    label={`${importPreview.filter(r => r.exists).length} already exist (will be skipped)`}
                    color="warning" size="small"
                  />
                )}
              </Box>

              {/* Progress bar while importing */}
              {importing && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" gutterBottom>Importing... {importProgress}%</Typography>
                  <LinearProgress variant="determinate" value={importProgress} />
                </Box>
              )}

              {/* Preview table */}
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Status</strong></TableCell>
                      <TableCell><strong>Product Name</strong></TableCell>
                      <TableCell><strong>Item Code</strong></TableCell>
                      <TableCell><strong>Category</strong></TableCell>
                      <TableCell align="center"><strong>Qty</strong></TableCell>
                      <TableCell align="right"><strong>Cost (LKR)</strong></TableCell>
                      <TableCell align="right"><strong>Retail (LKR)</strong></TableCell>
                      <TableCell align="right"><strong>Wholesale (LKR)</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {importPreview.map((row, i) => (
                      <TableRow
                        key={i}
                        sx={{ opacity: row.exists ? 0.5 : 1, backgroundColor: row.exists ? 'action.hover' : 'inherit' }}
                      >
                        <TableCell>
                          {row.exists ? (
                            <Tooltip title="Already exists — will be skipped">
                              <Chip label="Skip" size="small" color="warning" variant="outlined" />
                            </Tooltip>
                          ) : (
                            <Chip label="New" size="small" color="success" variant="outlined" />
                          )}
                        </TableCell>
                        <TableCell><Typography variant="body2" fontWeight={600}>{row.name}</Typography></TableCell>
                        <TableCell><Typography variant="body2" color="text.secondary">{row.itemCode || '—'}</Typography></TableCell>
                        <TableCell>{row.category}</TableCell>
                        <TableCell align="center">{row.qty}</TableCell>
                        <TableCell align="right">{row.cost.toFixed(2)}</TableCell>
                        <TableCell align="right">{row.retail > 0 ? row.retail.toFixed(2) : '—'}</TableCell>
                        <TableCell align="right">{row.wholesale > 0 ? row.wholesale.toFixed(2) : '—'}</TableCell>
                      </TableRow>
                    ))}
                    {importPreview.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={8} align="center" sx={{ py: 3 }}>
                          <Typography color="text.secondary">No valid rows found</Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeImportDialog}>
            {importResult ? 'Close' : 'Cancel'}
          </Button>
          {!importResult && (
            <Button
              variant="contained"
              onClick={handleConfirmImport}
              disabled={importing || importPreview.filter(r => !r.exists).length === 0}
              startIcon={importing ? <CircularProgress size={16} color="inherit" /> : <Upload size={16} />}
            >
              {importing ? `Importing... ${importProgress}%` : `Import ${importPreview.filter(r => !r.exists).length} Product(s)`}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Inventory;
